/**
 * Walks the linked list from leaf to root, calls each scope's `data()` getter,
 * and assembles the final event:
 *
 * - `name` is the caller's literal `options.event`, verbatim
 * - `data` is the leaf `options.data` spread at the root, plus `scopePath`
 *   (the dotted chain, omitted when no scopes are mounted) and `scopeData`
 *   (transport + scope bags keyed by name)
 *
 * Duplicate scope names are disambiguated inside `scopeData` with a depth
 * suffix (`list`, `list_2`) so key collisions never silently overwrite earlier
 * entries. `scopePath` is unaffected — it reflects the literal chain.
 *
 * Pure function, no React dependency — easy to test.
 */
export function mergeScopeChain(leaf, transport, options) {
    // Walk leaf -> root, then reverse to get root-first order.
    const chain = [];
    for (let node = leaf; node !== null; node = node.parent) {
        chain.push(node);
    }
    chain.reverse();
    const scopeData = {};
    // Transport data is keyed under the transport name. It appears in
    // `scopeData` like any scope, but never contributes to `scopePath`.
    if (transport.data !== undefined) {
        scopeData[transport.name] = transport.data;
    }
    const segments = [];
    const seen = new Set([transport.name]);
    for (const node of chain) {
        const key = uniqueKey(node.name, seen);
        segments.push(node.name);
        scopeData[key] = node.data ? node.data() : undefined;
    }
    const scopePath = segments.join('.');
    return {
        name: options.event,
        data: {
            ...options.data,
            // Written after the leaf spread so the framework wins any collision;
            // `LeafInstrumentationData` reserves these keys at the type level too.
            ...(scopePath === '' ? {} : { scopePath: scopePath }),
            scopeData: scopeData,
        },
    };
}
/**
 * Returns `name` if unseen, otherwise the next available `${name}_${i}` suffix.
 * Mutates `seen` to include the chosen key.
 */
function uniqueKey(name, seen) {
    if (!seen.has(name)) {
        seen.add(name);
        return name;
    }
    for (let i = 2;; i++) {
        const candidate = `${name}_${i}`;
        if (!seen.has(candidate)) {
            seen.add(candidate);
            return candidate;
        }
    }
}
//# sourceMappingURL=mergeScopeChain.js.map